import React, { useState } from 'react';
import { View, Text, StyleSheet, SafeAreaView, TouchableOpacity, ScrollView, Image } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Colors, SIZES } from '../constants/theme';
import { useFoodStore } from '../store/useFoodStore';
import { idDeCategoria, nombreDeCategoria } from '../constants/categoriasDeNegocio';
import { volver } from '../utils/volver';
import { pesos } from '../utils/dinero';

export default function FoodCategoryScreen() {
  const router = useRouter();
  const { category, icon } = useLocalSearchParams<{ category: string, icon: string }>();
  const restaurants = useFoodStore(state => state.restaurants);

  const [activeFilter, setActiveFilter] = useState<string>('Todos');

  const FILTERS = ['Todos', 'Mejor Calificados', 'Delivery Gratis', 'Más Rápidos', 'A - Z'];

  // ── EL APANO DE ANTES SE QUEDO CORTO, Y POR ESO SE CAMBIA ──────────────
  // Aqui habia un `normalizarCategoria` que quitaba el prefijo «comida » para
  // que 'Comida Dominicana' casara con 'Dominicana'. Arreglaba ESTA pantalla y
  // dejaba rota la portada (food.tsx), que cruza con `===` y ademas filtra en el
  // SERVIDOR con `where('category','==',…)`, donde no hay normalizacion posible.
  // Ahora las dos pantallas cruzan por ID contra la misma lista, y
  // `idDeCategoria` entiende tambien el texto viejo mientras queden documentos
  // sin migrar.
  const idBuscado = idDeCategoria(category);
  let filteredRestaurants = restaurants.filter(
    (r) => idBuscado !== null && idDeCategoria(r.category) === idBuscado
  );

  // Aplicar lógica Premium: Los que pagaron el plan salen de primeros siempre
  filteredRestaurants = filteredRestaurants.sort((a, b) => {
    if (a.isPremium && !b.isPremium) return -1;
    if (!a.isPremium && b.isPremium) return 1;
    return 0;
  });

  // Apply secondary filters
  if (activeFilter === 'Mejor Calificados') {
    filteredRestaurants = filteredRestaurants.filter(r => r.rating >= 4.5);
  } else if (activeFilter === 'Delivery Gratis') {
    filteredRestaurants = filteredRestaurants.filter(r => r.deliveryFee === 0);
  } else if (activeFilter === 'Más Rápidos') {
    // deliveryTime ejemplo "15-25 min". Defensivo ante undefined o formatos raros.
    filteredRestaurants = filteredRestaurants.sort((a, b) => {
      const timeA = parseInt((a.deliveryTime ?? '').split('-')[0], 10) || 99;
      const timeB = parseInt((b.deliveryTime ?? '').split('-')[0], 10) || 99;
      return timeA - timeB;
    });
  } else if (activeFilter === 'A - Z') {
    filteredRestaurants = filteredRestaurants.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backBtn} onPress={() => volver(router)}>
          <Ionicons name="arrow-back" size={24} color={Colors.text} />
        </TouchableOpacity>
        {/* `category` ahora es el ID. Pintarlo tal cual sacaria «local» o
            «cafe» en la cabecera. */}
        <Text style={styles.headerTitle}>{icon} {nombreDeCategoria(category) ?? category}</Text>
        <View style={{ width: 40 }} />
      </View>

      <View style={styles.filtersWrapper}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.filtersContainer}>
          {FILTERS.map((filter, index) => (
            <TouchableOpacity 
              key={index}
              style={[styles.filterChip, activeFilter === filter && styles.filterChipActive]}
              onPress={() => setActiveFilter(filter)}
            >
              <Text style={[styles.filterText, activeFilter === filter && styles.filterTextActive]}>{filter}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        {filteredRestaurants.length === 0 ? (
          <View style={styles.emptyState}>
            <Text style={{ fontSize: 64, marginBottom: 10 }}>{icon}</Text>
            {/* Sin promesas a futuro. El texto anterior decía que los
                restaurantes "pronto publicarán su menú aquí" — Google ya le
                citó a esta cuenta el patrón "coming soon" como Broken
                Functionality. Un estado vacío describe el presente, no promete
                el futuro. */}
            <Text style={styles.emptyTitle}>No hay negocios en esta categoría</Text>
            <Text style={styles.emptySubtitle}>Por ahora no hay negocios de {nombreDeCategoria(category) ?? category} disponibles en tu zona.</Text>
            <TouchableOpacity style={styles.btnAviso} onPress={() => volver(router)}>
              <Text style={styles.btnAvisoText}>Explorar otras opciones</Text>
            </TouchableOpacity>
          </View>
        ) : (
          filteredRestaurants.map(restaurant => (
            <TouchableOpacity 
              key={restaurant.id} 
              style={styles.restaurantCard}
              onPress={() => router.push(`/restaurant/${restaurant.id}` as any)}
              activeOpacity={0.9}
            >
              <Image source={{ uri: restaurant.coverUrl }} style={styles.restaurantCover} />
              <View style={styles.restaurantInfo}>
                <View style={styles.restaurantHeader}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                    <Text style={styles.restaurantName} numberOfLines={1}>{restaurant.name}</Text>
                    {restaurant.isPremium && (
                      <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#FFF8E1', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 8, marginLeft: 8, borderWidth: 1, borderColor: '#FFE082' }}>
                        <Ionicons name="star" size={10} color="#FF9500" />
                        <Text style={{ fontSize: 9, fontWeight: 'bold', color: '#FF9500', marginLeft: 2, textTransform: 'uppercase' }}>Recomendado</Text>
                      </View>
                    )}
                  </View>
                  <View style={styles.ratingBadge}>
                    <Text style={styles.ratingText}>{restaurant.rating}</Text>
                    <Ionicons name="star" size={12} color="#FF9500" />
                  </View>
                </View>
                {/* El nombre, no el id. Y si falta la categoria o el tiempo, no se
                    deja el «•» huerfano colgando. */}
                <Text style={styles.restaurantDesc}>
                  {[nombreDeCategoria(restaurant.category), restaurant.deliveryTime].filter(Boolean).join(' • ')}
                </Text>
                <Text style={styles.deliveryFee}>Delivery: {pesos(restaurant.deliveryFee)}</Text>
              </View>
            </TouchableOpacity>
          ))
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#FFF' },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20, paddingTop: 10, paddingBottom: 15, borderBottomWidth: 1, borderBottomColor: Colors.border },
  backBtn: { padding: 8, marginLeft: -8 },
  headerTitle: { fontSize: 18, fontWeight: 'bold', color: Colors.text },
  
  filtersWrapper: { borderBottomWidth: 1, borderBottomColor: Colors.border, backgroundColor: '#FAFAFA' },
  filtersContainer: { paddingHorizontal: 20, paddingVertical: 12, gap: 10 },
  filterChip: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20, backgroundColor: '#FFF', borderWidth: 1, borderColor: '#E0E0E0' },
  filterChipActive: { backgroundColor: Colors.primary, borderColor: Colors.primary },
  filterText: { fontSize: 13, fontWeight: '600', color: Colors.textSecondary },
  filterTextActive: { color: '#FFF' },

  content: { padding: 20, paddingBottom: 50, flexGrow: 1 },
  
  emptyState: { flex: 1, justifyContent: 'center', alignItems: 'center', marginTop: 100 },
  emptyTitle: { fontSize: 20, fontWeight: 'bold', color: Colors.text, marginBottom: 10 },
  emptySubtitle: { fontSize: 15, color: Colors.textSecondary, textAlign: 'center', paddingHorizontal: 20, lineHeight: 22, marginBottom: 30 },
  btnAviso: { backgroundColor: Colors.primary, paddingHorizontal: 20, paddingVertical: 12, borderRadius: 12 },
  btnAvisoText: { color: '#FFF', fontWeight: 'bold', fontSize: 16 },

  restaurantCard: { backgroundColor: '#FFF', borderRadius: 16, overflow: 'hidden', marginBottom: 20, borderWidth: 1, borderColor: Colors.border, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.05, shadowRadius: 8, elevation: 2 },
  restaurantCover: { width: '100%', height: 140, backgroundColor: '#EEE' },
  restaurantInfo: { padding: 15 },
  restaurantHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 },
  restaurantName: { fontSize: 16, fontWeight: 'bold', color: Colors.text },
  ratingBadge: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#FFF8E1', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 12 },
  ratingText: { fontSize: 12, fontWeight: 'bold', color: '#FF9500', marginRight: 4 },
  restaurantDesc: { fontSize: 13, color: Colors.textSecondary, marginBottom: 8 },
  deliveryFee: { fontSize: 13, fontWeight: 'bold', color: Colors.primary },
});
